import React, { useState, useEffect, useRef } from "react";

import { useNavigate, useNavigationType } from "react-router-dom";
import { signIn, confirmSignIn } from "aws-amplify/auth";

import { useAuth } from "../context/AuthProvider";
import I18n from "../helpers/I18n";
import {
  REST_API_URL,
  KAKAO,
  NAVER,
  LOG_IN_COOKIE,
  GUEST_ORDER_URL_PATH,
} from "../constants";
import API from "../service/API";
import { deleteCookie, getCookie, setCookie } from "../helpers/cookie";
import { LoadingElement } from "../helpers/utils";
import { Marketing, Privacy, TermsOfUse } from "../helpers/policies";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";

const BENEFITS = [
  {
    key: "coupon",
    label: "신규 가입 축하 쿠폰 10,000원",
  },
  {
    key: "coin",
    label: "출석 체크만 해도 적립 포인트를 드려요",
  },
  {
    key: "heart",
    label: "매일 만나는 행운! 최대 5,000원 포인트",
  },
  {
    key: "ship",
    label: "하나만 구매해도 전 제품 무료 배송",
  },
];

const terms = [
  {
    key: "tos",
    title: "서비스 이용 약관 동의",
    required: true,
    body: <TermsOfUse />,
  },
  {
    key: "privacy",
    title: "개인정보 수집 및 이용 동의",
    required: true,
    body: <Privacy />,
  },
  {
    key: "marketing",
    title: "개인정보 수집 및 이용 및 이벤트/마케팅 수신 동의",
    required: false,
    body: <Marketing />,
  },
];

const terms_nobody = [
  {
    key: "age",
    title: "만 14세 이상입니다.",
    required: true,
    body: "", // 본문 없음(체크만)
    noBody: true,
  },
];

const SocialSignup = React.memo(
  ({
    onNext,
    allOk,
    setAll,
    requiredOk,
    checked,
    toggle,
    birth,
    setBirth,
    onSignup,
    onSignin,
    step,
    i18n,
  }) => {
    switch (step) {
      case 1:
        return (
          <section className="terms__wrap">
            <header className="terms__head">
              <h2 className="terms__title">{i18n("signup.step1.title")}</h2>
              <p className="terms__subtitle">{i18n("signup.step1.subtitle")}</p>
            </header>

            <form>
              <div className="terms__cards">
                {terms.map((t) => (
                  <article className={"terms-card"} key={t.key}>
                    <label className="terms-card__head">
                      <input
                        type="checkbox"
                        checked={checked[t.key]}
                        onChange={() => toggle(t.key)}
                      />
                      <div className="terms-card__title">
                        {t.required ? (
                          <em className="req">{i18n("common.requiredTag")}</em>
                        ) : (
                          <em className="opt">{i18n("common.optionalTag")}</em>
                        )}
                        <div className="h6">
                          {i18n(`signup.terms.${t.key}.title`)}
                        </div>
                      </div>
                    </label>
                    <div className="terms-card__body" aria-live="polite">
                      <div className="terms-card__scroll">
                        {t.body}
                        <br />
                        <br />
                        {t.body}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {terms_nobody.map((t) => (
                <article className={"terms-card narrow"} key={t.key}>
                  <label className="terms-card__head">
                    <input
                      type="checkbox"
                      checked={checked[t.key]}
                      onChange={() => toggle(t.key)}
                    />
                    <div className="terms-card__title">
                      {t.required ? (
                        <em className="req">{i18n("common.requiredTag")}</em>
                      ) : (
                        <em className="opt">{i18n("common.optionalTag")}</em>
                      )}
                      <div className="h6">
                        {i18n(`signup.terms.${t.key}.title`)}
                      </div>
                    </div>
                  </label>
                </article>
              ))}

              <div className="dashed-divider" />

              {/* 전체 동의 */}
              <label className="terms__agree-all">
                <input
                  type="checkbox"
                  checked={allOk}
                  onChange={(e) => setAll(e.target.checked)}
                />
                <div className="h6">
                  <strong>GRANHAND</strong> {i18n("signup.terms.agreeAll")}
                </div>
              </label>

              {/* 버튼 */}
              <button
                onClick={onNext}
                className="login__btn login__btn--outline"
                disabled={!requiredOk}
              >
                {i18n("signup.terms.next")}
              </button>
            </form>
          </section>
        );

      case 2:
        return (
          <div className="signup-2">
            <div className="h5">{i18n("signup.step2.subtitle")}</div>
            <form className="login__form" style={{ marginTop: "30px" }}>
              <div className="login__label h6" htmlFor="birth">
                {i18n("signup.form.birth.label")}
              </div>
              <input
                id="birth"
                type="date"
                className="login__input"
                placeholder={i18n("signup.form.birth.placeholder")}
                value={birth}
                min="1900-01-01"
                max="2099-12-31"
                onChange={(e) => setBirth(e.target.value)}
              />
              <button
                type="button"
                onClick={onSignup}
                disabled={!birth}
                aria-disabled={!birth}
                className="login__btn login__btn--primary"
                style={{ marginTop: "80px" }}
              >
                {i18n("signup.cta.submit")}
              </button>
            </form>
          </div>
        );

      case 3:
        return (
          <div className="signup-5">
            <div
              className="h3 bold"
              style={{ textAlign: "center", color: "#6f6963" }}
            >
              {i18n("signup.complete.title")}
            </div>
            <div className="benefit-wrapper">
              {BENEFITS.map((b) => (
                <div key={b.key} className="benefit-line">
                  <img
                    alt={i18n("signup.benefit.iconAlt")}
                    src={require(`../img/icon/${b.key}.png`)}
                    className="icon"
                  />
                  {i18n(`signup.benefit.${b.key}`)}
                </div>
              ))}
            </div>
            <button
              onClick={onSignin}
              className="login__btn login__btn--outline"
              style={{ marginTop: "80px" }}
            >
              {i18n("signup.complete.backToShop")}
            </button>
          </div>
        );

      default:
        return null;
    }
  }
);

export default function Signin() {
  const nav = useNavigate();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'
  const { user, refresh } = useAuth();
  const { i18n, cafe24RedirectUrl } = useLang();
  const [isLoading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [birth, setBirth] = useState(null);
  const [isSocialSignup, setSocialSignup] = useState(null);
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(terms.map((t) => [t.key, false]))
  );
  const tempAccount = useRef();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keep, setKeep] = useState(false);

  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO.API_KEY);
    }
    getKakaoSession();
    getNaverSession();

    window.result = async function (payload) {
      fetch(
        `${REST_API_URL}/mok/result?txId=${encodeURIComponent(
          JSON.parse(payload).txId
        )}`
      )
        .then((r) => r.json())
        .then((json) => {});
    };

    return () => {
      delete window.result;
    };
  }, []);

  const getKakaoSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "kakao") {
      deleteCookie(LOG_IN_COOKIE);
      setLoading(true);
      fetch(`${REST_API_URL}${KAKAO.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            doKakaoSiginin(data.me);
          } else {
            setLoading(false);
          }
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  };

  const getNaverSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "naver") {
      deleteCookie(LOG_IN_COOKIE);
      setLoading(true);
      fetch(`${REST_API_URL}${NAVER.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            doNaverSiginin(data.me.response);
          } else {
            setLoading(false);
          }
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  };

  const handleSignup = async () => {
    const account = tempAccount.current;
    await API.createUser(
      account.email,
      account.name,
      account.phone,
      null,
      null,
      account.method,
      account.dataId,
      checked.marketing
    );
    let authUser = await signIn({
      username: account.email,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
      },
    });
    const answer = authUser.nextStep?.additionalInfo?.answer;
    await confirmSignIn({ challengeResponse: answer });
    setSocialSignup(3);
  };

  const handleSocialSignupComplete = async () => {
    await refresh();
  };

  const doKakaoSiginin = async (data) => {
    try {
      const account = data.kakao_account;
      const phone = account.phone_number.replace(/[\s-]/g, ""); // +82 10-1234-1234
      const u = await API.checkUser(account.email, phone, "KAKAO", data.id);
      if (!u) {
        tempAccount.current = {
          email: account.email,
          name: account.name,
          phone: phone,
          dataId: data.id,
          method: "KAKAO",
        };
        setSocialSignup(1);
      } else {
        const providerType = u.attributes?.["custom:provider"];
        if (providerType && providerType.toLowerCase() === "kakao") {
          let authUser = await signIn({
            username: u.username,
            options: {
              authFlowType: "CUSTOM_WITHOUT_SRP",
            },
          });
          const answer = authUser.nextStep?.additionalInfo?.answer;
          await confirmSignIn({ challengeResponse: answer });
          await refresh();
        } else {
          nav("/existing-account", {
            state: {
              user: u,
            },
          });
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.name === "NotAuthorizedException") {
        setErrorMessage(i18n("signin.result.fail"));
      }
      setLoading(false);
    }
  };

  const doNaverSiginin = async (data) => {
    try {
      const phone = data.mobile_e164;
      const u = await API.checkUser(data.email, phone, "NAVER", data.id);
      if (!u) {
        tempAccount.current = {
          email: data.email,
          name: data.name,
          phone: phone,
          dataId: data.id,
          method: "NAVER",
        };
        setSocialSignup(1);
      } else {
        const providerType = u.attributes?.["custom:provider"];
        if (providerType && providerType.toLowerCase() === "naver") {
          let authUser = await signIn({
            username: u.username,
            options: {
              authFlowType: "CUSTOM_WITHOUT_SRP",
            },
          });
          const answer = authUser.nextStep?.additionalInfo?.answer;
          await confirmSignIn({ challengeResponse: answer });
          await refresh();
        } else {
          nav("/existing-account", {
            state: {
              user: u,
            },
          });
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.name === "NotAuthorizedException") {
        setErrorMessage(i18n("signin.result.fail"));
      }
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      doSignIn();
    }
  };

  const doSignIn = async () => {
    setLoading(true);
    setMessage("");
    try {
      await signIn({ username: email, password });
      setMessage(i18n("signin.result.success")); // "로그인 성공"
      await refresh();
      setLoading(false);
    } catch (e) {
      const status = await API.getUserStatus(email);
      if (status === "FORCE_CHANGE_PASSWORD") {
        nav(`/find-account?type=password`, {
          state: { email },
        });
      } else {
        if (e.name === "NotAuthorizedException") {
          if (e.message === "Incorrect username or password.") {
            setErrorMessage(i18n("signin.result.incorrect"));
          } else {
            setErrorMessage(
              i18n("signin.result.fail") // "로그인 실패"
            );
          }
        } else if (e.name === "UserNotFoundException") {
          if (e.message === "User does not exist.") {
            setErrorMessage(i18n("signin.result.notExist"));
          } else {
            setErrorMessage(
              i18n("signin.result.fail") // "로그인 실패"
            );
          }
        } else {
          setErrorMessage(
            i18n("signin.result.fail") // "로그인 실패"
          );
        }
        setLoading(false);
      }
    }
  };

  const handleSignupClick = () => {
    nav("/signup");
  };

  const handleGuestOrderTrack = () => {
    window.location.replace(cafe24RedirectUrl + GUEST_ORDER_URL_PATH);
  };

  const loginWIthNaver = () => {
    setLoading(true);
    setCookie(LOG_IN_COOKIE, "naver");
    const redirectUri = `${REST_API_URL}${NAVER.CALLBACK_PATH}`;
    const state = crypto.randomUUID();
    const naverAuthUrl = `${NAVER.AUTH_URL}?response_type=code&client_id=${
      NAVER.CLIENT_ID
    }&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;

    window.location.href = naverAuthUrl;
  };

  const loginWithKakao = () => {
    if (!window.Kakao) return;
    setLoading(true);
    setCookie(LOG_IN_COOKIE, "kakao");
    window.Kakao.Auth.authorize({
      redirectUri: `${REST_API_URL}${KAKAO.CALLBACK_PATH}`,
    });
  };

  const handleBack = () => {
    window.location.replace(cafe24RedirectUrl);
  };

  const handleFindClick = (type) => {
    nav(`/find-account?type=${type}`);
  };

  const handleNext = () => {
    setSocialSignup(isSocialSignup + 1);
  };

  const requiredOk = terms
    .filter((t) => t.required)
    .every((t) => checked[t.key]);
  const allOk = terms.every((t) => checked[t.key]);
  const toggle = (key) =>
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const setAll = (val) =>
    setChecked(
      Object.fromEntries([...terms, ...terms_nobody].map((t) => [t.key, val]))
    );

  if (isLoading) return <LoadingElement />;

  return isSocialSignup !== null ? (
    <main className="signup">
      <div className="flex-between">
        <button
          className="topbar__back"
          onClick={handleBack}
          aria-label={i18n("signin.nav.back.aria")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="topbar__back-text h6">{i18n("signin.nav.back")}</div>
        </button>
        <LanguageDropdown />
      </div>

      <div className="flex-center">
        <img
          alt="logo"
          src={require("../img/granhand_logo.png")}
          className="top-logo"
        />
      </div>

      <SocialSignup
        onNext={handleNext}
        checked={checked}
        toggle={toggle}
        allOk={allOk}
        setAll={setAll}
        requiredOk={requiredOk}
        birth={birth}
        setBirth={setBirth}
        onSignup={handleSignup}
        onSignin={handleSocialSignupComplete}
        step={isSocialSignup}
        i18n={i18n}
      />
    </main>
  ) : (
    <main className="login">
      <div className="flex-between" style={{ marginBottom: "10px" }}>
        <button
          className="topbar__back"
          onClick={handleBack}
          style={{ marginBottom: 0 }}
          aria-label={i18n("signin.nav.back.aria")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="topbar__back-text h6">{i18n("signin.nav.back")}</div>
        </button>
        <LanguageDropdown />
      </div>

      <div className="flex-center">
        <img
          alt="logo"
          src={require("../img/granhand_logo.png")}
          className="top-logo"
        />
      </div>

      <form className="login__form">
        <div className="login__label h6" htmlFor="email">
          {i18n("signin.form.id.label")}
        </div>
        <input
          id="email"
          type="email"
          className="login__input"
          placeholder={i18n("signin.form.email.placeholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />

        <div className="login__label h6" htmlFor="password">
          {i18n("signin.form.password.label")}
        </div>
        <input
          id="password"
          type="password"
          className="login__input"
          onKeyDown={handleKeyDown}
          placeholder={i18n("signin.form.password.placeholder")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />

        {/* 로그인 상태 유지 */}
        <label className="login__keep">
          <input
            type="checkbox"
            checked={keep}
            onChange={(e) => setKeep(e.target.checked)}
          />
          <span>{i18n("signin.form.keepLogin")}</span>
        </label>

        {errorMessage ? (
          <div className="h6 red-color">{errorMessage}</div>
        ) : null}

        <div className="action-wrapper">
          <button
            type="button"
            onClick={doSignIn}
            disabled={!email || !password}
            aria-disabled={!email || !password}
            className="login__btn login__btn--primary"
          >
            {i18n("signin.cta.login")}
          </button>

          <button
            type="button"
            className="login__btn login__btn--outline"
            onClick={handleSignupClick}
          >
            {i18n("signin.cta.signup")}
          </button>
          <button
            type="button"
            className="login__btn login__btn--outline"
            onClick={handleGuestOrderTrack}
          >
            {i18n("signin.cta.anonymous")}
          </button>

          <div className="login__links">
            <button
              type="button"
              className="link-btn"
              onClick={() => handleFindClick("id")}
            >
              {i18n("signin.find.id")}
            </button>
            <span className="login__divider_for_find" aria-hidden>
              ｜
            </span>
            <button
              type="button"
              className="link-btn"
              onClick={() => handleFindClick("password")}
            >
              {i18n("signin.find.password")}
            </button>
          </div>
        </div>
      </form>

      {/* 간편 로그인 */}
      <section className="login__social">
        <div className="login__divider" aria-hidden="true">
          <div className="h6">{i18n("signin.social.title")}</div>
        </div>

        <div className="login__social-row">
          <div
            onClick={loginWIthNaver}
            className="social social--naver"
            aria-label={i18n("signin.social.naver.aria")}
          >
            <img alt="naver" src={require("../img/social/signin_naver.png")} />
          </div>
          <div
            onClick={loginWithKakao}
            className="social social--kakao"
            aria-label={i18n("signin.social.kakao.aria")}
          >
            <img alt="kakao" src={require("../img/social/signin_kakao.png")} />
          </div>
        </div>
      </section>
    </main>
  );
}
